"use client";

/**
 * Landing Page - Navigation Component
 */

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Menu } from "lucide-react";
import { Button } from "@/shared/presentation/components/ui/button";
import { siteConfig } from "@/config/site";
import { NAV_LINKS, LANDING_IMAGES, LANDING_ROUTES } from "../../constants";
import { MobileMenu } from "./MobileMenu";
import { ThemeSwitcher } from "@/shared";

export function Navigation() {
  const t = useTranslations();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  return (
    <>
      <nav className="mx-auto flex h-auto w-full max-w-5xl justify-between gap-8 overflow-hidden rounded-none bg-background py-4">
        {/* Logo */}
        <div className="my-auto pl-4 lg:pl-2">
          <span className="flex cursor-default items-center justify-center gap-2 text-2xl font-bold uppercase text-primary-dark">
            <Image
              src={LANDING_IMAGES.logo}
              alt={`${siteConfig.name} logo`}
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <span className="text-xl font-bold uppercase text-secondary">
              {siteConfig.name}
            </span>
          </span>
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden flex-1 items-center justify-start gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={link.href}
              className="my-auto px-2 py-2 text-base font-semibold capitalize tracking-[1px] text-primary-dark transition-all hover:rounded-lg hover:text-primary"
            >
              {t(link.labelKey)}
            </a>
          ))}
        </div>

        {/* Desktop Dashboard Button */}
        <div className="my-auto hidden pr-4 lg:flex gap-2 lg:pr-2">
          <Link href={LANDING_ROUTES.dashboard}>
            <Button
              variant="default"
              className="rounded-full bg-secondary px-6 py-2 font-bold text-white transition-all hover:bg-primary hover:bg-opacity-90"
            >
              {t("landing.nav.dashboard")}
            </Button>
          </Link>
          <ThemeSwitcher />
        </div>

        {/* Mobile Menu Button */}
        <div className="my-auto block pr-4 lg:hidden lg:pr-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className="rounded p-2 px-4 font-bold text-secondary transition-all hover:bg-secondary hover:bg-opacity-90 hover:text-white"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <MobileMenu onClose={toggleMenu} />}
    </>
  );
}

export default Navigation;
